import React from 'react';
import { Box, Typography } from '@mui/material';
import { VaultItemDTO } from '../../types';
import { createParchmentSurface, hashStringToSeed, parchmentUnderlineSx } from '../../theme/surfaces';
import { useReferenceDataStore } from '../../stores/referenceDataStore';
import { useWarbandStore } from '../../stores/warbandStore';
import { VaultItemActionMenu } from './VaultItemActionMenu';

interface GrimoireCardProps {
  item: VaultItemDTO;
  memberNameById: Map<string, string>;
  onDiscard: (instanceId: string) => void;
  onSell?: (instanceId: string) => void;
}

export const GrimoireCard: React.FC<GrimoireCardProps> = React.memo(function GrimoireCard({
  item,
  memberNameById,
  onDiscard,
  onSell,
}) {
  const getSpellById = useReferenceDataStore((s) => s.getSpellById);
  const allAvailableSpells = useWarbandStore((s) => s.allAvailableSpells);

  const invokedSpell = React.useMemo(() => {
    if (!item.invokedSpellId) return undefined;
    return allAvailableSpells.find((s) => s.id === item.invokedSpellId) ?? getSpellById(item.invokedSpellId);
  }, [item.invokedSpellId, allAvailableSpells, getSpellById]);

  const surfaceSx = React.useMemo(
    () => ({ ...createParchmentSurface(hashStringToSeed(item.id)), maxWidth: '400px' }),
    [item.id],
  );

  return (
    <Box sx={{ height: '100%', ...surfaceSx }} p={1.5}>
      <Box sx={{ py: 1.5, '&:last-child': { pb: 1.5 }, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="subtitle2">{item.name}</Typography>
          {item.description && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ...parchmentUnderlineSx }}>
              {item.description}
            </Typography>
          )}
          {invokedSpell?.description && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {invokedSpell.description}
            </Typography>
          )}
          {invokedSpell && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Casting number: {invokedSpell.castingNumber} / {invokedSpell.castingNumber + 2}
            </Typography>
          )}
          {item.salePrice > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Sale: {item.salePrice}gc
            </Typography>
          )}
          {item.warbandMemberId && memberNameById.get(item.warbandMemberId) && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
              Used by: {memberNameById.get(item.warbandMemberId)}
            </Typography>
          )}
        </Box>
        <VaultItemActionMenu
          itemName={item.name}
          instanceId={item.instanceId!}
          salePrice={item.salePrice}
          onDiscard={onDiscard}
          onSell={onSell}
        />
      </Box>
    </Box>
  );
});
